import * as React from 'react';
import { ArrowUp, MessageCircle, Trash2 } from 'lucide-react';
import { z } from 'zod';
import {
  commentComposerRecipe,
  commentRowRecipe,
} from '../recipes/mobile.recipe';
import { Avatar } from './Avatar';
import { Button } from './Button';
import { Form, FormField, useZodForm } from './Form';
import { Icon } from './Icon';
import { Input } from './Input';
import { Stack } from './Layout';
import { Text } from './Text';

export interface CommentItem {
  id: string;
  /** Who wrote it. */
  author: string;
  /** Their photo; their initials stand in without one. */
  avatarSrc?: string | null;
  text: string;
  /** Still on its way to the server: shown faded, and not yet deletable. */
  pending?: boolean;
  /** The viewer may delete it: a bin appears at its right. */
  deletable?: boolean;
}

export interface CommentListProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  comments: CommentItem[];
  /** A heading over the comments. Left out, there is none. */
  heading?: React.ReactNode;
  /** What shows when there are none. */
  emptyLabel?: string;
  /** Called with a comment marked `deletable` when its bin is pressed. */
  onDelete?: (id: string) => void;
  /** The comment being deleted: its bin spins. */
  deletingId?: string | null;
}

/**
 * The comments on a photo, oldest first, as the Figma's Comment Appear
 * frames draw them: the writer's face, their name, and what they said
 * beneath it, the face top-aligned so a long comment wraps under the name.
 *
 *   <CommentList heading="Comments" comments={comments} onDelete={remove} />
 *
 * With none it says "No comments", centred, which is all the Figma's empty
 * photo sheet shows. A comment the viewer may delete carries a bin.
 */
export const CommentList = React.forwardRef<HTMLDivElement, CommentListProps>(
  function CommentList(
    {
      comments,
      heading,
      emptyLabel = 'No comments',
      onDelete,
      deletingId = null,
      className,
      ...props
    },
    ref
  ) {
    if (comments.length === 0)
      return (
        <Stack ref={ref} gap={4} className={className} {...props}>
          <Text variant="body-lg" tone="muted" align="center">
            {emptyLabel}
          </Text>
        </Stack>
      );
    return (
      <Stack ref={ref} gap={4} className={className} {...props}>
        {heading ? (
          <Text as="h3" variant="h3" tone="muted">
            {heading}
          </Text>
        ) : null}
        <Stack as="ul" gap={4} aria-label="Comments">
          {comments.map((comment) => (
            <li
              key={comment.id}
              className={commentRowRecipe({ pending: !!comment.pending })}
            >
              <Avatar name={comment.author} src={comment.avatarSrc} size="xs" />
              <Stack gap={0} grow>
                <Text as="span" variant="body-strong">
                  {comment.author}
                </Text>
                <Text variant="body">{comment.text}</Text>
              </Stack>
              {comment.deletable && !comment.pending && onDelete ? (
                <Button
                  variant="destructive-ghost"
                  size="icon-xs"
                  loading={deletingId === comment.id}
                  disabled={deletingId === comment.id}
                  aria-label="Delete comment"
                  onClick={() => onDelete(comment.id)}
                >
                  <Trash2 />
                </Button>
              ) : null}
            </li>
          ))}
        </Stack>
      </Stack>
    );
  }
);

export const commentSchema = z.object({
  text: z.string().trim().min(1, 'Write the comment first'),
});

export type CommentValues = z.infer<typeof commentSchema>;

export interface CommentComposerProps {
  /**
   * Called with the trimmed comment. The box clears once this resolves;
   * return `false` (or reject) to keep it, for a send that failed.
   */
  onSubmit: (text: string) => void | boolean | Promise<void | boolean>;
  /** A send is running: the button spins and will not press again. */
  sending?: boolean;
  placeholder?: string;
  /** Put the cursor in the box on mount: the sheet's Comment button. */
  autoFocus?: boolean;
}

/**
 * Writing a comment on a photo: one pill with a chat glyph, the words, and
 * a round send button inside it, as the Figma's Quick Comment frames draw
 * it. It sits at the foot of a sheet so the keyboard rises under it.
 *
 *   <CommentComposer onSubmit={(text) => addComment.mutateAsync(text)}
 *     sending={addComment.isPending} />
 *
 * The draft is the composer's own; the screen only sees the finished
 * comment, and an empty one is refused with a message.
 */
export function CommentComposer({
  onSubmit,
  sending = false,
  placeholder = 'Add a comment',
  autoFocus = false,
}: CommentComposerProps) {
  const form = useZodForm(commentSchema, { defaultValues: { text: '' } });

  const submit = async ({ text }: CommentValues) => {
    try {
      const kept = await onSubmit(text);
      if (kept !== false) form.reset({ text: '' });
    } catch {
      // The screen reported the failure; the draft stays for another go.
    }
  };

  return (
    <Form form={form} onSubmit={submit}>
      <FormField form={form} name="text">
        {(field) => (
          <span className={commentComposerRecipe()}>
            <Icon icon={MessageCircle} size="md" />
            <Input
              {...field}
              variant="bare"
              aria-label="Comment"
              placeholder={placeholder}
              autoFocus={autoFocus}
              autoComplete="off"
              enterKeyHint="send"
            />
            <Button
              type="submit"
              variant="primary"
              size="icon"
              pill
              loading={sending}
              aria-label="Send comment"
            >
              <ArrowUp />
            </Button>
          </span>
        )}
      </FormField>
    </Form>
  );
}
